import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box } from '@wix/design-system';
import { useControlService } from '../../common/atomic/hooks';
import type { AggregatedStyleValues } from '../../common/abstractStyles/types';
import {
  mapTextProps,
  mapTextEvent,
  type NamespaceTracker,
  getCssPropertyTypes,
} from './TextControlsAdapter';
import type { ComponentsPlatformService } from '@wix/editor-platform-manifest-services';
import {
  DefinitionType,
  VariableType,
} from '@wix/editor-elements-panel-service';
import { TEXT_PROPERTIES } from './constants';
import { TextControlPartials } from '../types';
import { getShorthandAtomics } from '../../common/grouping/propertyRelationships';
import { CSS_PROPERTIES } from '@wix/component-protocol/schema';
import { useBI } from '../../hooks/useBI';
import { GlobalDesignStyleGroupMetadata } from '../../types';
import { StylesDerivedNativeActions } from '@wix/editor-platform-host-integration-apis';

interface TextControlsProps {
  values: AggregatedStyleValues;
  updateStyles: (changes: Record<string, string>) => Promise<void>;
  componentsAPI: ComponentsPlatformService;
  metadata: GlobalDesignStyleGroupMetadata;
}

/**
 * Properties we want to SHOW in the text control group.
 * This only affects excludedControls (UI visibility), not controlConfigs or props.
 */
const SUPPORTED_TEXT_PROPERTIES = [
  'font',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'color',
  // Optional: add textAlign, fontStyle, textDecoration if needed
] as const;

const DEFAULT_EXCLUDED_CONTROLS = ['fontWeightToggle'];

/**
 * TextControls component for Global Design panel.
 *
 * Pattern (mirrors BorderControls):
 * 1. Get TextControlGroup from service
 * 2. Use mapTextProps to map: values → control props (with namespace mitigation)
 * 3. Use mapTextEvent to map: control onChange → namespaced CSS properties
 * 4. Pass changes to updateStyles for hierarchy propagation
 *
 * CRITICAL: Handles cssCustomProperties → cssProperties mitigation
 * to work around getControlGroup limitation.
 *
 * IMPORTANT: Configures ALL properties but shows only SUPPORTED_TEXT_PROPERTIES
 * via excludedControls (hides styleIcon, align, spacing, etc.)
 */
export const TextControls: React.FC<TextControlsProps> = ({
  values,
  updateStyles,
  componentsAPI: _componentsAPI, // Kept for API compatibility, no longer used after removing variable resolution
  metadata,
}) => {
  const service = useControlService();
  const { trackValueUpdate } = useBI();
  const [ControlGroup, setControlGroup] = useState<
    ReturnType<typeof service.getControlGroup>['Component'] | null
  >(null);
  // Track namespace mapping across renders
  const namespaceTrackerRef = useRef<NamespaceTracker>({});

  useEffect(() => {
    const cssPropertyTypes = getCssPropertyTypes(values);
    const fontProperties = new Set(
      getShorthandAtomics(CSS_PROPERTIES.CSS_PROPERTY_TYPE.font),
    );

    const controlConfigs = TEXT_PROPERTIES.reduce<
      Record<string, { propertyKey: string; namespace: 'cssPropertyType' }>
    >((configs, propertyKey) => {
      const isShorhandPropertyWithDefinedFontProperty =
        fontProperties.has(propertyKey) &&
        cssPropertyTypes.has(CSS_PROPERTIES.CSS_PROPERTY_TYPE.font);
      if (
        cssPropertyTypes.has(propertyKey) &&
        !isShorhandPropertyWithDefinedFontProperty
      ) {
        configs[propertyKey] = {
          propertyKey,
          namespace: 'cssPropertyType' as const,
        };
      }
      return configs;
    }, {});

    const { Component } = service.getControlGroup({
      controlGroupType: 'TextControlGroup',

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      controlConfigs: controlConfigs as any,

      // Exclude disabled properties
      excludedControls,

      // Optional settings
      hideToggle: false,
    } as any);
    // Wrap in arrow function: React treats bare functions passed to setState
    // as state updaters and calls them with prevState (null), which crashes.
    setControlGroup(() => Component);
  }, [service]);

  // Map aggregated values to control props
  const { textProps, excludedControls } = useMemo(() => {
    const { props, namespaceTracker } = mapTextProps(values);

    // Store namespace tracker for event handler
    namespaceTrackerRef.current = namespaceTracker;

    // Build excluded controls list:
    // 1. Disabled properties (from props mapping)
    // 2. Properties NOT in SUPPORTED_TEXT_PROPERTIES (styleIcon, spacing, align, etc.)
    const supportedSet = new Set(SUPPORTED_TEXT_PROPERTIES);

    const excluded = new Set<string>(DEFAULT_EXCLUDED_CONTROLS);

    // Add disabled properties
    props.disabledProperties?.forEach((prop) => excluded.add(prop));

    // Add properties we don't want to show in UI
    TEXT_PROPERTIES.forEach((prop) => {
      if (!supportedSet.has(prop as any)) {
        excluded.add(prop);
      }
    });

    return {
      textProps: props.customPanelProps,
      excludedControls: Array.from(excluded),
    };
  }, [values]);

  // Handle control changes
  const handleChange = useCallback(
    (
      definitionType: DefinitionType,
      variableType: VariableType,
      standardValue: string,
      _customValue: unknown,
      partials?: TextControlPartials,
    ) => {
      // Map event to CSS changes with namespace routing
      const changes = mapTextEvent(
        definitionType,
        variableType,
        standardValue,
        namespaceTrackerRef.current,
        partials,
      );
      // Note: partials contains decomposed property values when available
      // For example, font changes may provide: { fontFamily, fontSize, fontWeight }
      // mapTextEvent uses partials when available, falls back to standardValue otherwise

      // Flatten for updateStyles (engine handles namespace routing internally)
      const flatChanges: Record<string, string> = {
        ...changes.cssProperties,
        ...changes.cssCustomProperties,
      };

      if (Object.keys(flatChanges).length > 0) {
        trackValueUpdate(StylesDerivedNativeActions.textStyles, {
          prevValues: JSON.stringify({
            ...textProps.cssProperties,
            ...textProps.cssCustomProperties,
          }),
          newValues: JSON.stringify(flatChanges),
          targetComponents: metadata.targetComponents,
        });
        updateStyles(flatChanges);
      }
    },
    [updateStyles, trackValueUpdate],
  );

  if (!ControlGroup || !service) {
    return null;
  }

  return (
    <Box direction="vertical" gap="4">
      <ControlGroup
        // @ts-expect-error: TODO: fix this
        customPanelProps={textProps}
        customOnChange={handleChange}
      />
    </Box>
  );
};
